import { UIEvent, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsSmallScreen from '@hooks/element-size-hooks/useIsSmallScreen';
import { selectedDateWithoutTime } from '@store/global/selectors';
import { setOrdersAndPagingInfo, resetOrdersAndCustomersStore } from '@pages/orders/store/slice';
import { selectOrdersPagingInfo, selectOrdersFilter } from '@pages/orders/store/selectors';
import { PagingOrdersData } from '@api/shipping-data/types/PagingOrdersData';
import { useLazyGetOrdersQuery } from '@api/shipping-data/ordersApi';
import { useResetCustomerOrdersMutation, useResetCustomerInfoMutation, useResetOrderTimelineMutation } from '@api/shipping-data/detailApi';
import { useLazyDayOrdersStatisticsSummaryQuery, useResetDayOrdersStatisticsSummaryMutation } from '@api/shipping-data/api';
import { AppPage } from 'components/app-page/AppPage';
import Statistics from '@pages/orders/statistics/Statistics';
import ListArea from '@pages/orders/orders-page/list-area/ListArea';
import Detail from '@pages/orders/detail/Detail';



export const Orders = () => {

	const pageRef = useRef<HTMLDivElement>(null);
	const isSmallScreen = useIsSmallScreen();
	const dateWithoutTime = useSelector(selectedDateWithoutTime);
	const ordersFilter = useSelector(selectOrdersFilter);
	const pagingInfo = useSelector(selectOrdersPagingInfo);

	const [ ordersScrollTop, setOrdersScrollTop ] = useState(0);
	const [ ordersTop, setOrdersTop ] = useState(0);
	const [ ordersHeight, setOrdersHeight ] = useState(0);
	const [ isScrolledDown, setIsScrolledDown ] = useState(false);


	const dispatch = useDispatch();
	const updateOrdersAndPagingInfo = (pagingOrdersData: PagingOrdersData) => dispatch(setOrdersAndPagingInfo(pagingOrdersData));
	const dispatchResetOrdersStore = () => dispatch(resetOrdersAndCustomersStore());
	
	const [ getOrders, { data, isLoading, isFetching, isError,  } ] = useLazyGetOrdersQuery();
	const [ getStatisticsSummary, { data: stData, isLoading: stIsLoading, isFetching: stIsFetching, isError: stIsError } ] = useLazyDayOrdersStatisticsSummaryQuery();
	const [ resetStatistics ] = useResetDayOrdersStatisticsSummaryMutation();
	const [ resetCustomerOrders ] = useResetCustomerOrdersMutation();
	const [ resetCustomerInfo ] = useResetCustomerInfoMutation();
	const [ resetOrderTimeline ] = useResetOrderTimelineMutation();


	useEffect(() => {
		if (pageRef && pageRef.current) {
			// init scroll values, before scroll event is triggered
			setOrdersTop(pageRef.current.getBoundingClientRect().top);
			setOrdersHeight(pageRef.current.clientHeight);
		}
		return () => {
			dispatchResetOrdersStore();
		}
	}, []);


	useEffect(() => {
		if (dateWithoutTime) {
			getOrdersInit();
			getStatisticsSummary(dateWithoutTime, true);
		}
	}, [ dateWithoutTime, ordersFilter ]);


	useEffect(() => {
		if (isScrolledDown) {
			getOrdersNextPage();
		}
	}, [ isScrolledDown ]);


	const getOrdersInit = useCallback(() => {
		if (dateWithoutTime) {
			getOrders({ date: dateWithoutTime, tileId: ordersFilter });
		}
	},[ dateWithoutTime, ordersFilter ]);


	const getOrdersNextPage = useCallback(() => {
		if (dateWithoutTime && pagingInfo && pagingInfo.hasNextPage && pagingInfo.continuationToken) {
			getOrders({ date: dateWithoutTime, tileId: ordersFilter, continuationToken: pagingInfo.continuationToken });
		}
	},[ dateWithoutTime, pagingInfo, ordersFilter ]);


	useEffect(() => {
		if (data) {
			updateOrdersAndPagingInfo(data);
		}
	}, [ data ]);

	
	const resetAllApi = useCallback(() => {
		getOrdersInit();
		resetStatistics();
		resetCustomerOrders();
		resetCustomerInfo();
		resetOrderTimeline();
	},[ getOrdersInit ]);


	return (
		<AppPage
			isError={isError || stIsError}
			isLoading={isLoading || stIsLoading || stIsFetching}
			className='orders-page'
			pageLabel='Objednávky'
			resetPageApi={resetAllApi}
			ref={pageRef}
			onScroll={(event: UIEvent<HTMLDivElement>) => {
				const bottom = event.currentTarget.scrollHeight - event.currentTarget.scrollTop < event.currentTarget.clientHeight + 400;
				setIsScrolledDown(bottom);
				setOrdersScrollTop(event.currentTarget.scrollTop);
				setOrdersTop(event.currentTarget.getBoundingClientRect().top);
				setOrdersHeight(event.currentTarget.clientHeight);
			}}
		>
			<div className='orders-content'>
				
				<Statistics statistics={stData} />
				
				<ListArea isSmallScreen={isSmallScreen} pagingLoading={data && data.pagingInfo.hasNextPage && isFetching ? true : false} />
				
				{!isSmallScreen &&
					<Detail
						dateWithoutTime={dateWithoutTime}
						isSmallScreen={isSmallScreen}
						ordersScrollTop={ordersScrollTop}
						ordersTop={ordersTop}
						ordersHeight={ordersHeight} />}
			</div>
		
		</AppPage>
	)
}

export default Orders;